import React, { useMemo } from "react";
import { CirclePlus, Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import ButtonV2 from "../../../../components/ui/button/v2";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import { AreaOfFraud } from "../../../../api/cases";
import { Stakeholder } from "../../../../api/stakeholders";
import { RELATED_AS_LABELS, RelatedAs } from "../../../../api/stakeholder_relationships";
import { Account } from "../../../../api/accounts";
import FormField, { useTouched } from "./FormField";
import AccountPartyRelationGroupRow, { AccountRelationshipGroup } from "./AccountPartyRelationRow";
import { EmployeeDetails } from "../../../../api/enrichedStakeholder";

export const getAccountPartyErrors = (
  party: AddAccountPartyRow,
  allAdded: AddAccountPartyRow[],
  allExisting?: Stakeholder[]
) => {
  const relations: Record<string, string> = {};
  const account_numbers: Record<string, string> = {};
  let name: string | undefined;

  const partyName = party.stakeholder?.name || "";
  const normalizedPartyName = partyName.replace(/\s+/g, "").toLowerCase();

  if (!partyName.trim()) {
    name = "Party name is required.";
  } else if (
    allAdded.filter(
      (p) => (p.stakeholder?.name || "").replace(/\s+/g, "").toLowerCase() === normalizedPartyName
    ).length > 1
  ) {
    name = "Party name must be unique.";
  } else if (
    allExisting &&
    allExisting.some((s) => (s.name || "").replace(/\s+/g, "").toLowerCase() === normalizedPartyName)
  ) {
    name = "Party already exists in this case.";
  }

  const relationshipGroups = party.relationshipGroups || [];
  relationshipGroups.forEach((r) => {
    if (!r.relatedAs) relations[r.id] = "Party type is required.";
    if (!r.accountNumbers || r.accountNumbers.length === 0) account_numbers[r.id] = "At least 1 account is required.";
    if (!r.relatedAs || !r.accountNumbers ||  r.accountNumbers.length === 0) return;

    const isDuplicate = relationshipGroups.some(
      (other) =>
        other.id !== r.id &&
        other.relatedAs === r.relatedAs &&
        other.accountNumbers.some(a => r.accountNumbers.indexOf(a) !== -1)
    );
    if (isDuplicate) {
      account_numbers[r.id] = `Account already added for ${RELATED_AS_LABELS[r.relatedAs]}`;
    }
  });

  return { name, relations, account_numbers };
};

export const isAccountPartyValid = (
  party: AddAccountPartyRow,
  allAdded: AddAccountPartyRow[],
  allExisting?: Stakeholder[]
) => {
  const { name, relations, account_numbers } = getAccountPartyErrors(party, allAdded, allExisting);
  return (
    !name &&
    Object.keys(relations).length === 0 &&
    Object.keys(account_numbers).length === 0 &&
    party.relationshipGroups.length > 0
  );
};

export interface AddAccountPartyRow {
  stakeholder: Stakeholder;
  relationshipGroups: AccountRelationshipGroup[];
  employee_details?: EmployeeDetails;
}

export interface AccountPartyRowProps {
  party: AddAccountPartyRow;
  allAdded: AddAccountPartyRow[];
  allExisting?: Stakeholder[];
  accounts: Account[];
  area_of_fraud: AreaOfFraud;
  showErrors: boolean;
  onChange: (party: AddAccountPartyRow) => void;
  onDelete: () => void;
}

export const AccountPartyRow: React.FC<AccountPartyRowProps> = ({
  party,
  allAdded,
  allExisting,
  accounts,
  area_of_fraud,
  showErrors,
  onChange,
  onDelete,
}) => {
  const { isTouched, markTouched } = useTouched();

  const accountOptions: OptionNameValuePair<string>[] = useMemo(
    () =>
      accounts
        .filter((acc) => acc.ext_account_number?.trim())
        .map((acc) => ({
          name: acc.ext_account_number.trim(),
          value: acc.account_number,
        })),
    [accounts]
  );

  const errors = getAccountPartyErrors(party, allAdded, allExisting);
  const show = (key: string) => showErrors || isTouched(key);
  const hasNameError = Boolean(show("name") && errors.name);

  const canAddRelationship = Object.keys(errors.relations).length === 0 && Object.keys(errors.account_numbers).length === 0;

  const isPrimaryAccountHolder = party.relationshipGroups.some((r) => r.relatedAs === RelatedAs.PRIMARY_ACCOUNT_HOLDER);

  const handleNameChange = (name: string) => {
    onChange({
      ...party,
      stakeholder: { ...party.stakeholder, name },
    });
  };

  const handleGroupChange = (group: AccountRelationshipGroup) => {
    const updatedGroups = party.relationshipGroups.map(g => (g.id === group.id ? group : g));
    onChange({
      ...party,
      relationshipGroups: updatedGroups
    })
  };

  const handleRemoveGroup = (group: AccountRelationshipGroup) => {
    const updatedGroups = party.relationshipGroups.filter(g => (g.id !== group.id));

    onChange({
      ...party,
      relationshipGroups: updatedGroups
    });
  };

  const handleAddRelationGroup = () => {
    onChange({
      ...party,
      relationshipGroups: [
        ...party.relationshipGroups,
        {id: uuidv4(), relatedAs: undefined, accountNumbers: []}
      ],
    });
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-gray-100 bg-white p-3">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
        <div className="group relative">
          <FormField
            type="text"
            label="Party Name"
            required
            disabled={isPrimaryAccountHolder}
            placeholder="Enter name"
            value={party.stakeholder?.name || ""}
            error={show("name") ? errors.name : undefined}
            onChange={handleNameChange}
            onBlur={() => markTouched("name")}
          />
          {isPrimaryAccountHolder && (
            <div className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-max max-w-[400px] rounded-[6px] bg-[#1F2937] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
              Account Name already provided in Accounts Section. To edit, please change the Account Name in the Accounts Section
            </div>
          )}
        </div>
        <div className="flex items-center pt-5">
          <ButtonV2
            variant="outline"
            size="sm"
            onClick={onDelete}
            title="Delete party"
            className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
            prefix={<Trash2 width="16" height="16" />}
          />
        </div>
      </div>

      <div className="relative flex flex-col gap-3 pl-[20px]">
        {party.relationshipGroups.map((group, index) => {
          return (
            <AccountPartyRelationGroupRow
              key={group.id}
              group={group}
              allGroups={party.relationshipGroups}
              areaOfFraud={area_of_fraud}
              accountOptions={accountOptions}
              onChange={handleGroupChange}
              onDelete={() => handleRemoveGroup(group)}
              showErrors={showErrors}
              relationError={errors.relations[group.id]}
              accountNumbersError={errors.account_numbers[group.id]}
              isFirst={index === 0}
              isLast={index === party.relationshipGroups.length - 1}
              parentHasError={hasNameError}
            />
          );
        })}

        <button
          type="button"
          disabled={!canAddRelationship}
          className="flex items-center gap-1 text-xs text-gray-500 self-start hover:text-[#3551F3] disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={handleAddRelationGroup}
        >
          <CirclePlus width={14} height={14} /> Add another
        </button>
      </div>
    </div>
  );
};

export default AccountPartyRow;